import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import PageShell from "../components/PageShell";
import NotFound from "./NotFound";
import { setMeta } from "../lib/seo";
import { SAMPLE_ITINERARIES, getSampleItinerary } from "../data/sampleItineraries";

export default function SampleItinerary() {
  const { slug } = useParams();
  const trip = getSampleItinerary(slug);

  useEffect(() => {
    if (!trip) return;
    setMeta({
      title: `${trip.title} — Sample Itinerary | Culinera`,
      description: trip.metaDescription,
      canonical: `https://culinera.ca/sample-itineraries/${trip.slug}`,
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: `${trip.title} — Sample Itinerary`,
        description: trip.metaDescription,
        author: { "@type": "Organization", name: "Culinera" },
        publisher: { "@type": "Organization", name: "Culinera" },
        mainEntityOfPage: `https://culinera.ca/sample-itineraries/${trip.slug}`,
      },
    });
  }, [trip]);

  if (!trip) return <NotFound />;

  const other = SAMPLE_ITINERARIES.find((s) => s.slug !== trip.slug);

  return (
    <PageShell testid={`sample-itinerary-${trip.slug}`}>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <nav className="text-sm text-gray-500 mb-4">
          <a href="/sample-itineraries" className="hover:text-teal-600">Sample Itineraries</a>
          <span className="mx-2">/</span>
          <span className="text-gray-700">{trip.title}</span>
        </nav>

        <span className="text-xs font-semibold text-teal-600 uppercase tracking-wide">{trip.duration} · {trip.city}</span>
        <h1 className="text-4xl font-bold text-gray-900 mt-2 mb-4">Sample Itinerary: {trip.title}</h1>

        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 mb-8" data-testid="sample-free-banner">
          <p className="text-teal-800 text-sm">
            This is a sample of the kind of trip the Culinera planner builds. Read it free — no email, no signup.{" "}
            <a href="/?view=wizard" className="font-semibold underline hover:text-teal-900">Build your own from the homepage.</a>
          </p>
        </div>

        <p className="text-lg text-gray-700 mb-8">{trip.intro}</p>

        <div className="space-y-8">
          {trip.days.map((d, i) => (
            <section key={i} data-testid={`sample-day-${i + 1}`}>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">{d.title}</h2>
              <p className="text-gray-700">{d.body}</p>
            </section>
          ))}
        </div>

        <div className="mt-10 bg-gray-50 border border-gray-200 rounded-lg p-5">
          <p className="text-gray-700"><strong>Practical notes:</strong> {trip.notes}</p>
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <a href="/?view=wizard" data-testid="sample-build-cta" className="bg-teal-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors">Build your own itinerary</a>
          {other && (
            <a href={`/sample-itineraries/${other.slug}`} data-testid="sample-other-cta" className="border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors">
              See the {other.title}
            </a>
          )}
        </div>
      </article>
    </PageShell>
  );
}
